import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { kycCompletedStyles } from './KYCCompleted.styles';
import { KYC_STATUS_VARIANTS, isKycUnderReviewStatus } from 'constants/kycStatusVariants';
import { KYCConstants } from 'constants/application';
import { isPostSubmitUnderReviewStatus } from 'utils/kycStatusRouting';

const checkmarkPath = "M75.1743 34.1417L46.514 69.977L24 51.2131L28.2479 46.1156L45.5582 60.5386L69.9971 30L75.1743 34.1417V34.1417Z";

export function KYCCompleted({ hasGstCertificate, nameMatched }: { hasGstCertificate?: boolean; nameMatched?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const info = useSelector((state: any) => state?.info?.allData);

  useEffect(() => {
    // Under-review must not bounce to OTP ("/"). Other lost sessions can.
    if (!info?.customerId && !isKycUnderReviewStatus(info?.kycStatus)) {
      navigate('/', { replace: true });
    }
  }, []); // eslint-disable-line

  const isVerifiedKyc =
    info?.kycStatus === KYC_STATUS_VARIANTS.VERIFIED ||
    info?.kycStatus === KYC_STATUS_VARIANTS.VERIFIED_NOT_SUBMITTED;

  const showGstNameMismatchMessage =
    KYCConstants.ENABLE_GST_SECTION && hasGstCertificate && nameMatched === false;

  // Submit now lands in PENDING_REVIEW — only a genuine VERIFIED row keeps the old completion copy.
  const showSubmittedUnderReview =
    showGstNameMismatchMessage || isPostSubmitUnderReviewStatus(info?.kycStatus);

  const title = showSubmittedUnderReview
    ? t('kyc_submitted_title', { defaultValue: 'KYC Submitted' })
    : t('kyc_complete', { defaultValue: 'KYC Completed' });

  const subtitle = showSubmittedUnderReview
    ? t('kyc_under_review_body', {
        defaultValue:
          'Your KYC documents are under review and cannot be edited at this time.',
      })
    : t('kyc_done_text', {
        defaultValue: 'Thank you for completing your KYC. Please close this window',
      });

  return (
    <Box sx={kycCompletedStyles.root(isVerifiedKyc && !showSubmittedUnderReview)}>
      <Box sx={kycCompletedStyles.content(isVerifiedKyc && !showSubmittedUnderReview)}>
        <Box sx={kycCompletedStyles.iconWrap}>
          <svg fill="none" viewBox="0 0 100 100">
            <circle cx="50" cy="50" fill="#007CBF" r="50" />
            <path clipRule="evenodd" d={checkmarkPath} fill="white" fillRule="evenodd" />
          </svg>
        </Box>
        <Box sx={kycCompletedStyles.textWrap}>
          <Typography sx={kycCompletedStyles.title}>
            {title}
          </Typography>
          <Typography sx={kycCompletedStyles.subtitle}>
            {subtitle}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
